'use client';

import React, { useState } from 'react';
import { Plus, User, TrendingUp, Users, X, Check, Phone } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { getTranslations } from '@/lib/i18n';
import MetricCard from '@/components/ui/MetricCard';

interface SalesRep {
  id: string;
  name: string;
  phone: string;
  zone: string;
  retailers: string[];
  monthlySales: number;
  commissionRate: number;
  commissionEarned: number;
  active: boolean;
}

const repsData: SalesRep[] = [
  { id: 'rep1', name: 'Ahmed Benali', phone: '+212 661-100200', zone: 'Centre_Ville', retailers: ['Snack Atlas Guéliz', 'Café Majorelle', 'Restaurant Fès Marrakech'], monthlySales: 42500, commissionRate: 3, commissionEarned: 1275, active: true },
  { id: 'rep2', name: 'Fatima Zahra Idrissi', phone: '+212 662-300400', zone: 'Ville_Haute', retailers: ['Épicerie Bab Jdid', 'Boulangerie Menara'], monthlySales: 18200, commissionRate: 3.5, commissionEarned: 637, active: true },
  { id: 'rep3', name: 'Youssef Tazi', phone: '+212 663-500600', zone: 'Khwazat', retailers: ['Superette Khwazat', 'Café Atlas'], monthlySales: 9800, commissionRate: 2.5, commissionEarned: 245, active: false },
];

export default function SalesRepsContent() {
  const { language, dir } = useLanguage();
  const t = getTranslations(language);
  const [reps, setReps] = useState<SalesRep[]>(repsData);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', zone: 'Centre_Ville', commissionRate: '3' });
  const [selected, setSelected] = useState<SalesRep | null>(null);

  const totalSales = reps.reduce((s, r) => s + r.monthlySales, 0);
  const totalCommission = reps.reduce((s, r) => s + r.commissionEarned, 0);
  const totalRetailers = reps.reduce((s, r) => s + r.retailers.length, 0);

  const addRep = () => {
    if (!form.name.trim()) return;
    setReps([...reps, { id: `rep${Date.now()}`, name: form.name, phone: form.phone, zone: form.zone, retailers: [], monthlySales: 0, commissionRate: Number(form.commissionRate), commissionEarned: 0, active: true }]);
    setForm({ name: '', phone: '', zone: 'Centre_Ville', commissionRate: '3' });
    setShowForm(false);
  };

  return (
    <div className="space-y-6 fade-in" dir={dir}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-700 text-foreground">{t('reps.title')}</h1>
          <p className="text-sm text-muted-foreground mt-0.5">{t('reps.subtitle')}</p>
        </div>
        <button onClick={() => setShowForm(true)} className="btn-primary flex-shrink-0">
          <Plus size={16} /> {t('reps.add')}
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard label={language === 'ar' ? 'المندوبون النشطون' : 'Représentants actifs'} value={String(reps.filter(r => r.active).length)} subValue={`/ ${reps.length}`} variant="positive" icon={<User size={18} />} />
        <MetricCard label={language === 'ar' ? 'إجمالي التجار' : 'Total détaillants'} value={String(totalRetailers)} subValue={language === 'ar' ? 'في المحافظ' : 'dans les portefeuilles'} variant="info" icon={<Users size={18} />} />
        <MetricCard label={language === 'ar' ? 'مبيعات الشهر' : 'Ventes du mois'} value={totalSales.toLocaleString()} subValue={t('common.mad')} variant="positive" icon={<TrendingUp size={18} />} />
        <MetricCard label={language === 'ar' ? 'إجمالي العمولات' : 'Total commissions'} value={totalCommission.toLocaleString()} subValue={t('common.mad')} variant="warning" icon={<TrendingUp size={18} />} />
      </div>

      {showForm && (
        <div className="bg-card border border-border rounded-xl p-5 shadow-card space-y-4">
          <h3 className="font-600 text-foreground">{t('reps.add')}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input className="input-base" placeholder={t('common.name')} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
            <input className="input-base" placeholder={t('common.phone')} value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
            <select className="input-base" value={form.zone} onChange={e => setForm({ ...form, zone: e.target.value })}>
              {['Centre_Ville', 'Ville_Haute', 'Khwazat'].map(z => <option key={z} value={z}>{z.replace(/_/g, ' ')}</option>)}
            </select>
            <input className="input-base" type="number" placeholder={`${t('reps.commission')} (%)`} value={form.commissionRate} onChange={e => setForm({ ...form, commissionRate: e.target.value })} />
          </div>
          <div className="flex gap-2">
            <button onClick={addRep} className="btn-primary"><Check size={16} /> {t('common.save')}</button>
            <button onClick={() => setShowForm(false)} className="btn-secondary"><X size={16} /> {t('common.cancel')}</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {reps.map((rep) => (
          <div key={rep.id} className="bg-card border border-border rounded-xl p-5 shadow-card card-hover">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl gradient-primary flex items-center justify-center text-white font-700 text-sm flex-shrink-0">
                  {rep.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                </div>
                <div>
                  <p className="font-700 text-foreground text-sm">{rep.name}</p>
                  <p className="text-2xs text-muted-foreground flex items-center gap-1"><Phone size={10} /> {rep.phone}</p>
                </div>
              </div>
              <span className={`text-2xs font-600 px-2 py-0.5 rounded-full ${rep.active ? 'text-success bg-success/10' : 'text-muted-foreground bg-muted'}`}>
                {rep.active ? t('common.active') : t('common.inactive')}
              </span>
            </div>
            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">{t('common.zone')}</span>
                <span className="font-600">{rep.zone.replace(/_/g, ' ')}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">{t('reps.sales')}</span>
                <span className="font-700 text-primary font-tabular">{rep.monthlySales.toLocaleString()} {t('common.mad')}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">{t('reps.commission')} ({rep.commissionRate}%)</span>
                <span className="font-700 text-success font-tabular">{rep.commissionEarned.toLocaleString()} {t('common.mad')}</span>
              </div>
            </div>
            <div>
              <p className="text-2xs text-muted-foreground mb-2">{t('reps.retailers')} ({rep.retailers.length})</p>
              <div className="flex flex-wrap gap-1">
                {rep.retailers.slice(0, 3).map(r => (
                  <span key={r} className="text-2xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full">{r}</span>
                ))}
                {rep.retailers.length > 3 && <span className="text-2xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full">+{rep.retailers.length - 3}</span>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
